/**
 * Journal post content.
 *
 * TO ADD A NEW POST: append an object to the `posts` array below. Newest
 * posts should go first — the index page renders them in array order.
 * Everything the index card and the post page need lives in this one object,
 * so no component changes are required for a new entry.
 */

/** A run of text. Add `to` for an internal link, `href` for an external one,
 *  `bold` to emphasize, or `italic` for a signoff. */
export type Segment =
  | string
  | {
      text: string
      /** Internal route path, e.g. '/menu'. Rendered as a react-router Link. */
      to?: string
      /** External URL. Opens in a new tab with rel="noopener noreferrer". */
      href?: string
      bold?: boolean
      italic?: boolean
    }

export type Block =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; content: Segment[] }
  | { type: 'list'; items: Segment[][] }

export type Post = {
  slug: string
  /** Displayed on the page and in cards. */
  title: string
  /** Human-readable date, e.g. 'July 28, 2026'. */
  date: string
  /** ISO 8601 date for the Article schema's datePublished. */
  datePublished: string
  author: string
  /** Short summary for the index card. */
  excerpt: string
  /** <title> for the post page. */
  metaTitle: string
  /** meta[name=description] for the post page. */
  metaDescription: string
  body: Block[]
  /** Closing italic byline, rendered below the body. */
  byline?: string
}

export const posts: Post[] = [
  {
    slug: 'our-story',
    title: 'The Story of Grounded',
    date: 'July 30, 2026',
    datePublished: '2026-07-30',
    author: 'Grounded',
    excerpt:
      "We're a small, local family who wanted to create a space where neighbors could come together over a delicious cup of coffee. This is our story.",
    metaTitle:
      'The Story of Grounded - A Family Coffee Shop in Livermore, CA',
    metaDescription:
      "The story of Grounded - a small local family who opened a Livermore coffee shop as a place for neighbors to gather over great coffee, inspired by the community cafes they've loved.",
    body: [
      {
        type: 'paragraph',
        content: [
          'The origin story for Grounded is simple. We\u2019re a small, local family who wanted to create a space where neighbors could come together and celebrate our community over a delicious cup of coffee.',
        ],
      },
      {
        type: 'paragraph',
        content: [
          'There was no grand plan. In fact, neither of us came from the coffee industry. Our north star was simply the memories of all the wonderful local coffee shops we\u2019ve visited whenever we traveled. Cozy, welcoming spaces. No master design, but a unique perspective of the town they\u2019re embodied within.',
        ],
      },
      {
        type: 'paragraph',
        content: ['We think of coffee shops like:'],
      },
      {
        type: 'list',
        items: [
          [
            {
              text: 'Third Space Coffeehouse in Bonneville, KS',
              href: 'https://thirdspacecoffeehouse.com/',
            },
          ],
          [
            {
              text: 'Downtown Local in Pescadero, CA',
              href: 'https://www.yelp.com/biz/downtown-local-pescadero-2',
            },
          ],
          [
            {
              text: 'Hidden House Coffee in Santa Ana, CA',
              href: 'https://hiddenhousecoffee.com/',
            },
          ],
          [
            {
              text: 'Crema in Nashville, TN',
              href: 'https://crema-coffee.com/',
            },
          ],
          [
            {
              text: "Enoch's in Westside Manhattan, NY",
              href: 'https://enochs.nyc/',
            },
          ],
        ],
      },
      {
        type: 'paragraph',
        content: [
          'Each funky, eclectic, and just a perfect encapsulation of the community that surrounds it. And of course, we\u2019ve had our minds blown at specialty, third-wave shops like:',
        ],
      },
      {
        type: 'list',
        items: [
          [
            {
              text: 'Iwana Coffee in Las Vegas, NV',
              href: 'https://iwanacoffee.com/',
            },
          ],
          [
            {
              text: 'Kanso Coffee Lab in Munich, Germany',
              href: 'https://kansocoffeelab.com/',
            },
          ],
          [
            {
              text: 'Coffee Stain in Kuala Lumpur, MY',
              href: 'https://www.coffeestain.my/',
            },
          ],
        ],
      },
      {
        type: 'paragraph',
        content: [
          'Those were our inspiration and the driver to open Grounded Livermore.',
        ],
      },
      {
        type: 'paragraph',
        content: [
          'In Grounded, we wanted a communal space where good company is met by high-quality coffee and nourishment. We sought to create a place where neighbors engage, and the artisanal talents of our community are showcased.',
        ],
      },
      {
        type: 'paragraph',
        content: [
          'Like all that is real in the world, our shop is not perfect. But that\u2019s almost exactly the intent. Our lives are not perfect. That\u2019s what makes us a community. We embrace, we celebrate, and we support each other through our successes and our imperfections.',
        ],
      },
      {
        type: 'paragraph',
        content: [{ text: '#StayGrounded', bold: true }],
      },
      {
        type: 'paragraph',
        content: [{ text: '\u2014 The Kungs', italic: true }],
      },
    ],
  },
]

export function getPostBySlug(slug: string): Post | undefined {
  return posts.find((post) => post.slug === slug)
}

/** Posts newest-first. The index page uses this so ordering never depends on
 *  where a new entry was pasted into the array. */
export function getPostsNewestFirst(): Post[] {
  return [...posts].sort((a, b) =>
    b.datePublished.localeCompare(a.datePublished),
  )
}
